import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { AdjustmentsHorizontalIcon, ExclamationTriangleIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';

import { AppDispatch } from '../../store';
import {
  fetchProspectLists,
  selectIsLoadingProspectLists,
  selectProspectLists,
} from '../../store/slices/nouveauxAdherentsSlice';

interface ProspectListSelectorProps {
  disabled?: boolean;
  selectedListIds: number[];
  onSelectedListIdsChange: (listIds: number[]) => void;
  excludeProspectsMode: 'siret' | 'siren';
  onExcludeProspectsModeChange: (mode: 'siret' | 'siren') => void;
}

const ProspectListSelector: React.FC<ProspectListSelectorProps> = ({
  disabled = false,
  selectedListIds,
  onSelectedListIdsChange,
  excludeProspectsMode,
  onExcludeProspectsModeChange,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const prospectLists = useSelector(selectProspectLists);
  const isLoadingProspectLists = useSelector(selectIsLoadingProspectLists);
  const hasFetchedProspectListsRef = useRef(false);

  useEffect(() => {
    if (hasFetchedProspectListsRef.current) {
      return;
    }
    if (prospectLists.length > 0 || isLoadingProspectLists) {
      return;
    }

    hasFetchedProspectListsRef.current = true;
    dispatch(fetchProspectLists());
  }, [dispatch, prospectLists.length, isLoadingProspectLists]);

  const toggleListId = (listId: number) => {
    if (selectedListIds.includes(listId)) {
      onSelectedListIdsChange(selectedListIds.filter((id) => id !== listId));
      return;
    }

    onSelectedListIdsChange([...selectedListIds, listId]);
  };

  const handleExcludeModeChange = (mode: 'siret' | 'siren') => {
    onExcludeProspectsModeChange(mode);
  };

  const isEmpty = prospectLists.length === 0;

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-800">Exclure des prospects existants</h3>
        <Link
          to="/prospect-lists"
          aria-label="Gérer les listes de prospects"
          title="Gérer les listes de prospects"
          className="inline-flex items-center justify-center rounded-md p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          <AdjustmentsHorizontalIcon className="h-5 w-5" />
        </Link>
      </div>

      <p className="mt-1 text-xs text-gray-500">
        Sélectionnez les listes de SIRET importées depuis vos outils tiers pour exclure ces prospects des résultats.
      </p>

      {isLoadingProspectLists ? (
        <div className="mt-4 space-y-2" aria-live="polite">
          <div className="h-8 rounded-md bg-gray-200 animate-pulse" />
          <div className="h-8 rounded-md bg-gray-200 animate-pulse" />
          <div className="h-8 rounded-md bg-gray-200 animate-pulse" />
        </div>
      ) : isEmpty ? (
        <div className="mt-4 flex items-start gap-3 rounded-md border border-blue-100 bg-blue-50 p-3 text-xs text-blue-800">
          <ExclamationTriangleIcon className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
          <div className="space-y-2">
            <p>
              Aucune liste de prospects importée. Rendez-vous dans l'onglet <strong>Listes prospects</strong> pour importer vos SIRET depuis un fichier Excel ou CSV.
            </p>
            <Link
              to="/prospect-lists"
              className="inline-flex items-center rounded-md bg-blue-600 px-2.5 py-1.5 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Accéder aux listes
            </Link>
          </div>
        </div>
      ) : (
        <div className="mt-3 space-y-3">
          <fieldset>
            <legend className="sr-only">Mode d'exclusion</legend>
            <div className="flex items-center gap-3">
              <label className={`inline-flex items-center text-xs rounded-md border px-3 py-1.5 ${excludeProspectsMode === 'siret' ? 'border-blue-500 bg-blue-50 text-blue-800' : 'border-gray-200 text-gray-700 hover:border-gray-300'}`}>
                <input
                  type="radio"
                  className="h-4 w-4 text-blue-600"
                  value="siret"
                  checked={excludeProspectsMode === 'siret'}
                  onChange={() => handleExcludeModeChange('siret')}
                  disabled={disabled}
                />
                <span className="ml-2">Exclure uniquement les SIRET listés</span>
              </label>
              <label className={`inline-flex items-center text-xs rounded-md border px-3 py-1.5 ${excludeProspectsMode === 'siren' ? 'border-blue-500 bg-blue-50 text-blue-800' : 'border-gray-200 text-gray-700 hover:border-gray-300'}`}>
                <input
                  type="radio"
                  className="h-4 w-4 text-blue-600"
                  value="siren"
                  checked={excludeProspectsMode === 'siren'}
                  onChange={() => handleExcludeModeChange('siren')}
                  disabled={disabled}
                />
                <span className="ml-2">Exclure toutes les entités (SIREN)</span>
              </label>
            </div>
            <p className="mt-1.5 text-[11px] text-gray-500">
              Avec le mode "SIRET listés", seules les entrées exactes sont exclues. En mode "SIREN", tous les établissements liés sont retirés des résultats.
            </p>
          </fieldset>

          <div className="max-h-56 overflow-y-auto space-y-2 border border-gray-200 rounded-md p-2 bg-white">
            {prospectLists.map((list) => {
              const isSelected = selectedListIds.includes(list.id);
              return (
                <button
                  key={list.id}
                  type="button"
                  disabled={disabled}
                  onClick={() => toggleListId(list.id)}
                  className={`w-full text-left text-xs rounded-md border p-3 transition-colors ${
                    isSelected ? 'border-blue-500 bg-blue-50 text-blue-800' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{list.name}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] text-gray-500">{list.sirets.length} SIRET</span>
                      {isSelected && <CheckIcon className="h-4 w-4 text-blue-600" aria-hidden="true" />}
                    </div>
                  </div>
                  {list.description && (
                    <p className="mt-1 text-[11px] text-gray-500 line-clamp-2">{list.description}</p>
                  )}
                </button>
              );
            })}
          </div>

          {selectedListIds.length > 0 && (
            <div className="space-y-2">
              <div className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-md p-2">
                {selectedListIds.length} liste{selectedListIds.length > 1 ? 's' : ''} sélectionnée{selectedListIds.length > 1 ? 's' : ''}.
              </div>
              <div className="flex flex-wrap gap-2">
                {prospectLists
                  .filter((l) => selectedListIds.includes(l.id))
                  .map((l) => (
                    <button
                      key={`chip-${l.id}`}
                      type="button"
                      disabled={disabled}
                      onClick={() => toggleListId(l.id)}
                      className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2.5 py-1 text-xs text-blue-800 hover:bg-blue-200"
                      aria-label={`Retirer ${l.name}`}
                    >
                      <span className="max-w-[12rem] truncate">{l.name}</span>
                      <XMarkIcon className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                  ))}
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => onSelectedListIdsChange([])}
                  className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-200"
                >
                  Effacer la sélection
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProspectListSelector;

